import { StandardEvents } from '@shopify/events';
import { beginCartLinesUpdate } from './standard-cart-events.js';

const root = window.Shopify?.routes?.root || '/';
let activeEditor;
let lastTrigger;
let openSequence = 0;
const drafts = new Map();

async function cartRequest(path, body) {
  const response = await fetch(`${root}${path}`, {
    method: body ? 'POST' : 'GET',
    headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const data = await response.json();
  if (!response.ok || data.status) throw new Error(data.description || data.message);
  return data;
}

function closeEditor() {
  if (!activeEditor) return;
  const input = activeEditor.querySelector('[data-ch-card-input]');
  if (input) drafts.set(activeEditor.dataset.lineKey, input.value);
  activeEditor.closest('dialog.cart-drawer__dialog')?.removeAttribute('data-ch-message-open');
  if (activeEditor instanceof HTMLDialogElement) activeEditor.close();
  activeEditor.remove();
  activeEditor = undefined;
  lastTrigger?.focus({ preventScroll: true });
}

async function openEditor(lineKey, variantId, trigger) {
  const sequence = ++openSequence;
  const cart = await cartRequest('cart.js');
  if (sequence !== openSequence) return;
  const line = cart.items.find((item) => lineKey ? item.key === lineKey : String(item.variant_id) === String(variantId) && item.product_type === 'Greetings Card');
  if (!line) return;
  closeEditor();
  lastTrigger = trigger;
  const template = document.querySelector('[data-ch-card-editor-template]');
  if (!template) return;
  const drawer = document.querySelector('cart-drawer-component dialog[open]');
  const desktop = window.matchMedia('(min-width: 990px)').matches && drawer;
  const host = document.createElement(desktop ? 'aside' : 'dialog');
  host.className = desktop ? 'ch-card-editor-sidecar' : 'ch-card-editor-dialog';
  host.dataset.lineKey = line.key;
  host.append(template.content.cloneNode(true));
  const editor = host.querySelector('.ch-card-editor');
  const heading = host.querySelector('[data-ch-card-heading]');
  heading.id = 'ch-active-card-editor-heading';
  host.setAttribute('aria-labelledby', heading.id);
  host.setAttribute('role', desktop ? 'region' : 'dialog');
  const image = host.querySelector('[data-ch-card-image]');
  const lineButton = Array.from(document.querySelectorAll('[data-ch-card-edit]')).find((button) => button.dataset.lineKey === line.key);
  const messageImage = lineButton?.dataset.messageImage || line.image;
  if (messageImage) image.src = messageImage;
  else image.hidden = true;
  const input = host.querySelector('[data-ch-card-input]');
  input.value = drafts.get(line.key) ?? line.properties?.['Card message'] ?? '';
  const count = host.querySelector('[data-ch-card-count]');
  const updateCount = () => { count.textContent = `${input.value.length} / 240`; };
  input.addEventListener('input', updateCount);
  updateCount();
  host.querySelector('[data-ch-card-close]').addEventListener('click', (event) => {
    // The desktop editor sits outside the drawer's bounds; keep this click
    // from reaching the drawer's outside-click handler after removing it.
    event.stopPropagation();
    closeEditor();
  });
  host.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    closeEditor();
  });
  host.addEventListener('cancel', (event) => { event.preventDefault(); closeEditor(); });
  host.querySelector('[data-ch-card-save]').addEventListener('click', async (event) => {
    const button = event.currentTarget;
    button.disabled = true;
    input.disabled = true;
    const status = host.querySelector('[data-ch-card-status]');
    status.textContent = '';
    let update;
    try {
      const current = await cartRequest('cart.js');
      const currentLine = current.items.find((item) => item.key === line.key);
      if (!currentLine) throw new Error('Card no longer in cart');
      const sections = [...new Set(Array.from(document.querySelectorAll('cart-items-component'), (item) => item.dataset.sectionId).filter(Boolean))];
      update = beginCartLinesUpdate(document, { action: 'update', context: 'cart', lines: [{ id: line.key, quantity: currentLine.quantity }], detail: { source: 'ch-card-message' } });
      const result = await cartRequest('cart/change.js', { id: line.key, quantity: currentLine.quantity, properties: { ...currentLine.properties, 'Card message': input.value.trim() }, sections: sections.join(','), sections_url: location.pathname });
      update.resolve(result, { sections: result.sections || {}, itemCount: result.item_count });
      drafts.delete(line.key);
      status.textContent = editor.dataset.saved;
      input.disabled = false;
      closeEditor();
      drafts.delete(line.key);
    } catch (error) {
      update?.reject(error);
      status.textContent = editor.dataset.error;
      button.disabled = false;
      input.disabled = false;
    }
  });
  if (desktop) {
    drawer.setAttribute('data-ch-message-open', '');
    drawer.append(host);
  } else {
    document.body.append(host);
    host.showModal();
  }
  activeEditor = host;
  host.querySelector('[data-ch-card-close]').focus({ preventScroll: true });
}

document.addEventListener('click', (event) => {
  const button = event.target.closest?.('[data-ch-card-edit]');
  if (!button) return;
  openEditor(button.dataset.lineKey, null, button).catch(console.error);
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && activeEditor && !(activeEditor instanceof HTMLDialogElement)) closeEditor();
});

async function openAddedCard(lineKey) {
  const drawer = document.querySelector('cart-drawer-component');
  const dialog = drawer?.querySelector('dialog');
  if (dialog && !dialog.open && typeof drawer.open === 'function') {
    document.querySelector('ch-cart-success-notification[open] [data-ch-cart-success-close]')?.click();
    drawer.open();
    if (!dialog.open) await new Promise((resolve) => {
      const observer = new MutationObserver(() => {
        if (!dialog.open) return;
        observer.disconnect();
        clearTimeout(timeout);
        resolve();
      });
      const timeout = setTimeout(() => { observer.disconnect(); resolve(); }, 1500);
      observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });
    });
  }
  await openEditor(lineKey);
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.action !== 'add' || !['product-form-component', 'ch-cart-upsells', 'ch-product-upsells'].includes(event.detail?.source)) return;
  // Product-page add-ons already offer a note field and can be edited in the
  // cart. Do not interrupt their add-to-cart flow with a second message editor.
  if (event.detail.source === 'ch-product-upsells' || (event.detail.source === 'product-form-component' && event.lines?.length > 1)) return;
  const addedVariants = new Set((event.lines || []).map((line) => String(line.merchandiseId).split('/').at(-1)));
  addedVariants.add(String(event.detail?.variantId));
  event.promise?.then(({ detail, userErrors }) => {
    if (detail?.didError || userErrors?.length) return;
    const line = detail?.ajaxCart?.items?.find((item) => addedVariants.has(String(item.variant_id)) && item.product_type === 'Greetings Card');
    if (!line || line.properties?.['Card message']?.trim()) return;
    window.setTimeout(() => openAddedCard(line.key).catch(console.error), 350);
  }).catch(() => {});
});

document.addEventListener('close', (event) => {
  if (activeEditor?.classList.contains('ch-card-editor-sidecar') && event.target.contains(activeEditor)) closeEditor();
}, true);

window.addEventListener('resize', () => {
  if (!activeEditor) return;
  const shouldBeSidecar = window.matchMedia('(min-width: 990px)').matches && document.querySelector('cart-drawer-component dialog[open]');
  if (Boolean(shouldBeSidecar) === activeEditor.classList.contains('ch-card-editor-sidecar')) return;
  const key = activeEditor.dataset.lineKey;
  closeEditor();
  openEditor(key).catch(console.error);
});
